import React, { useState, useEffect, useRef } from 'react';
import { User } from '../types';
import { sendFeedbackToAdmin, isLegacyLocalUser } from '../services/storageService';
import { isFirebaseReady, auth, firebaseDiagnostics } from '../services/firebase';
import { GoogleLoginButton } from './GoogleAuthModal';

interface SettingsScreenProps {
  user: User | null;
  onLogin: (user: User) => void;
  onLogout: () => void;
  processingStatus?: 'idle' | 'uploading' | 'analyzing' | 'generating_image' | 'saving' | 'completed' | 'error';
  onTestNotification?: () => void;
}

export const SettingsScreen: React.FC<SettingsScreenProps> = ({ 
    user, 
    onLogin, 
    onLogout, 
    processingStatus = 'idle',
    onTestNotification 
}) => {
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const [notifPermission, setNotifPermission] = useState<NotificationPermission>('default');

  // Diagnose voor deeplinks (welke modus / welk domein / service worker)
  const [swActive, setSwActive] = useState<boolean | null>(null);
  useEffect(() => {
      if (!('serviceWorker' in navigator)) { setSwActive(false); return; }
      navigator.serviceWorker.getRegistration().then(reg => setSwActive(!!reg?.active)).catch(() => setSwActive(false));
  }, []);
  const displayMode = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
      ? 'Geïnstalleerde app'
      : 'Browser';

  // Feedback State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubject, setFeedbackSubject] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState("suggestion"); // 'suggestion', 'bug', 'other'
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  useEffect(() => {
      if ('Notification' in window) {
          setNotifPermission(Notification.permission);
      }
      if (window.matchMedia('(display-mode: standalone)').matches) {
          setIsAppInstalled(true);
      }
      const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      setIsIOS(isIosDevice);
      const handleBeforeInstallPrompt = (e: any) => {
          e.preventDefault();
          setDeferredPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      return () => {
          window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
  }, []);

  const handleInstallClick = () => {
      if (deferredPrompt) {
          deferredPrompt.prompt();
          deferredPrompt.userChoice.then((choiceResult: any) => {
              if (choiceResult.outcome === 'accepted') {
                  setDeferredPrompt(null);
                  setIsAppInstalled(true);
                  setShowInstructions(false);
              }
          });
      } else {
          // Als er geen automatische prompt is (bijv. iOS of al weggedrukt), toggle de instructies
          setShowInstructions(prev => !prev);
      }
  };

  const handleRequestNotification = async () => {
      if (!('Notification' in window)) {
          alert("Je browser ondersteunt geen notificaties.");
          return;
      }
      try {
          const permission = await Notification.requestPermission();
          setNotifPermission(permission);
          if (permission === 'granted' && onTestNotification) {
             onTestNotification();
          }
      } catch (e) {
          console.error("Notification permission error", e);
      }
  };

  const handleCheckUpdate = () => {
    setIsCheckingUpdate(true);
    setTimeout(() => {
        window.location.reload();
    }, 1500);
  };



  const handleSendFeedback = async () => {
      if (!feedbackMessage.trim()) return;
      
      setIsSendingFeedback(true);
      try {
          await sendFeedbackToAdmin(user, feedbackSubject, feedbackMessage, feedbackType);
          setShowFeedbackModal(false);
          setFeedbackMessage("");
          setFeedbackSubject("");
          alert("Bedankt voor je feedback! We gaan ermee aan de slag.");
      } catch (e) {
          console.error(e);
          alert("Er ging iets mis bij het versturen. Controleer je verbinding.");
      } finally {
          setIsSendingFeedback(false);
      }
  };

  const getStatusLabel = () => {
      switch(processingStatus) {
          case 'idle': return 'Geen taken actief';
          case 'uploading': return 'Foto uploaden...';
          case 'analyzing': return 'AI Analyseert...';
          case 'generating_image': return 'Foto genereren...';
          case 'saving': return 'Opslaan...';
          case 'completed': return 'Klaar!';
          case 'error': return 'Fout';
          default: return 'Inactief';
      }
  };

  const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.6.0';

  return (
    <div className="flex flex-col w-full h-screen bg-background-light dark:bg-background-dark pb-32 overflow-y-auto no-scrollbar">
        <header className="flex-none z-10 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm px-4 pt-6 pb-2 border-b border-gray-100 dark:border-white/5">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="tracking-tight text-xl font-bold leading-tight">Instellingen</h1>
                    <p className="text-[10px] text-gray-400 -mt-0.5">Beheer je account en voorkeuren</p>
                </div>
                <div className="flex gap-1">
                    <span className="material-symbols-outlined text-gray-400 text-[24px]">settings</span>
                </div>
            </div>
        </header>

        <div className="p-4 pt-6 space-y-6">
            <section className="bg-surface-light dark:bg-surface-dark rounded-2xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                 <h2 className="text-lg font-bold text-text-primary-light dark:text-white mb-2">Systeem Status</h2>
                 <div className="flex items-center gap-3">
                     <div className={`w-3 h-3 rounded-full ${processingStatus === 'idle' ? 'bg-gray-300 dark:bg-gray-600' : processingStatus === 'error' ? 'bg-red-500' : 'bg-primary animate-pulse'}`}></div>
                     <span className="text-sm font-medium text-text-primary-light dark:text-white">{getStatusLabel()}</span>
                 </div>
            </section>

             {!isAppInstalled && (
                <section className="bg-primary/10 rounded-2xl p-4 border border-primary/20 transition-all">
                     <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-primary/20 p-2 rounded-full text-primary">
                                <span className="material-symbols-outlined text-xl">install_mobile</span>
                            </div>
                            <div className="flex flex-col">
                                <h3 className="text-sm font-bold text-primary">Installeer superMarct</h3>
                                <p className="text-[10px] text-gray-600 dark:text-gray-300">
                                    {deferredPrompt ? "Voor snellere toegang" : "Zet op je startscherm"}
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={handleInstallClick} 
                            className="px-4 py-2 bg-primary text-black font-bold text-xs rounded-xl shadow-lg active:scale-95 transition-transform"
                        >
                            Installeer
                        </button>
                     </div>

                     {/* Toon instructies als er geen automatische prompt is of als de gebruiker erop klikte */}
                     {(showInstructions) && (
                         <div className="mt-4 pt-3 border-t border-primary/10 animate-in fade-in slide-in-from-top-2">
                             {isIOS ? (
                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Tik onderaan op <span className="font-bold"><span className="material-symbols-outlined text-[12px] align-middle">ios_share</span> Deel</span> en kies vervolgens <span className="font-bold"><span className="material-symbols-outlined text-[12px] align-middle">add_box</span> Zet op beginscherm</span>.
                                </p>
                             ) : (
                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Tik op de browser instellingen (vaak <span className="material-symbols-outlined text-[12px] align-middle">more_vert</span>) en kies <strong>"App installeren"</strong> of <strong>"Toevoegen aan startscherm"</strong>.
                                </p>
                             )}
                         </div>
                     )}
                </section>
             )}

            <section className="bg-surface-light dark:bg-surface-dark rounded-2xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                <h2 className="text-lg font-bold text-text-primary-light dark:text-white mb-4">Mijn Account</h2>
                {user ? (
                    <div className="flex flex-col items-center text-center">
                        <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-200 mb-4 border-4 border-white dark:border-white/10 shadow-md">
                            {user.photoUrl ? (
                                <img src={user.photoUrl} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-primary text-background-dark text-2xl font-bold">
                                    {user.name.charAt(0)}
                                </div>
                            )}
                        </div>
                        <h3 className="text-xl font-bold text-text-primary-light dark:text-white">{user.name}</h3>
                        <p className="text-sm text-text-secondary-light dark:text-gray-400 mb-3">{user.email}</p>
                        
                        {isLegacyLocalUser(user) ? (
                            <div className="w-full mb-6 p-3 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-800/40 text-xs text-amber-900 dark:text-amber-200 text-left">
                                <div className="flex items-center gap-2 font-bold mb-1">
                                    <span className="material-symbols-outlined text-[18px]">sync_problem</span>
                                    <span>Account nog niet gekoppeld</span>
                                </div>
                                <p className="mb-3 leading-relaxed">Log opnieuw in met Google om synchronisatie en delen te activeren. Je lijsten en recepten blijven bewaard.</p>
                                <GoogleLoginButton onLogin={onLogin} label="Opnieuw inloggen met Google" />
                            </div>
                        ) : (
                            <div className="w-full mb-6 p-3 bg-primary/10 rounded-xl border border-primary/20 flex items-center justify-center gap-2 text-xs font-semibold text-primary">
                                <span className="material-symbols-outlined text-[18px]">cloud_done</span>
                                <span>Synchronisatie actief voor dit account</span>
                            </div>
                        )}

                        <button 
                            onClick={onLogout} 
                            className="w-full py-3 rounded-xl border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 font-bold text-sm hover:bg-red-50 dark:hover:bg-red-950/20 active:scale-[0.99] transition-all cursor-pointer"
                        >
                            Uitloggen
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col items-center text-center">
                        <div className="h-16 w-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-3xl text-gray-400">account_circle</span>
                        </div>
                        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Koppel je Google account</h3>
                        <p className="text-text-primary-light dark:text-gray-300 text-xs mb-6 max-w-xs">
                            Log in om je boodschappenlijstjes en bewaarde recepten te synchroniseren en AI-features optimaal te gebruiken.
                        </p>
                        <GoogleLoginButton onLogin={onLogin} label="Inloggen met Google" />
                    </div>
                )}
            </section>

            {/* App diagnose (deeplinks) */}
            <section className="bg-surface-light dark:bg-surface-dark rounded-2xl p-4 border border-gray-100 dark:border-white/5 shadow-sm text-xs">
                <h3 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[18px] text-gray-500">link</span>
                    App-status (links)
                </h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-600 dark:text-gray-300">
                    <dt className="font-semibold">Modus</dt><dd>{displayMode}</dd>
                    <dt className="font-semibold">Domein</dt><dd className="break-all">{window.location.origin}</dd>
                    <dt className="font-semibold">Service worker</dt><dd>{swActive === null ? '...' : swActive ? 'Actief' : 'Niet actief'}</dd>
                    <dt className="font-semibold">Firebase key</dt><dd className="break-all">{firebaseDiagnostics.keyStatus}</dd>
                    <dt className="font-semibold">Firebase project</dt><dd className="break-all">{firebaseDiagnostics.projectId}</dd>
                    <dt className="font-semibold">Auth domein</dt><dd className="break-all">{firebaseDiagnostics.authDomain}</dd>
                </dl>
                <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
                    Gedeelde links openen alleen in de app als het domein hierboven exact gelijk is aan het domein in de link.
                </p>
            </section>

            {/* Support & Feedback Section */}
            <section className="bg-surface-light dark:bg-surface-dark rounded-2xl overflow-hidden border border-gray-100 dark:border-white/5 shadow-sm">
                 <div className="p-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setShowFeedbackModal(true)}>
                    <div className="flex items-center gap-3">
                         <span className="material-symbols-outlined text-gray-500">mail</span>
                         <div>
                             <span className="text-sm font-medium block">Feedback & Ondersteuning</span>
                             <span className="text-[10px] text-gray-400">Meld een probleem of doe een suggestie</span>
                         </div>
                    </div>
                    <span className="material-symbols-outlined text-gray-400 text-lg">chevron_right</span>
                 </div>
                 
                 <div className="p-4 flex items-center justify-between hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3">
                         <span className="material-symbols-outlined text-gray-500">notifications</span>
                         <span className="text-sm font-medium">Notificaties</span>
                    </div>
                    {notifPermission === 'granted' ? <span className="text-[10px] font-bold text-green-500">AAN</span> : <button onClick={handleRequestNotification} className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-lg">Activeer</button>}
                 </div>
            </section>
            
            <div className="flex flex-col items-center gap-3 pt-4 pb-6">
                <button onClick={handleCheckUpdate} disabled={isCheckingUpdate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-light dark:bg-white/5 border border-gray-200 dark:border-white/10 text-xs font-bold transition active:scale-95 disabled:opacity-50">
                    <span className={`material-symbols-outlined text-[16px] ${isCheckingUpdate ? 'animate-spin' : ''}`}>{isCheckingUpdate ? 'sync' : 'system_update'}</span>
                    {isCheckingUpdate ? 'Controleren...' : 'Update controleren'}
                </button>
                <div className="flex flex-col items-center gap-1">
                    <span className="text-xs font-bold text-gray-400">superMarct v{appVersion}</span>
                    <span className="text-[10px] text-gray-300">Powered by Gemini & Firebase</span>
                </div>
            </div>
        </div>

        {/* Feedback Modal */}
        {showFeedbackModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowFeedbackModal(false)} />
                <div className="bg-white dark:bg-[#1A2E22] w-full max-w-sm rounded-[32px] p-6 shadow-2xl relative z-10 animate-in zoom-in-95 duration-200">
                    <div className="flex justify-between items-start mb-6">
                        <h3 className="text-xl font-bold">Feedback</h3>
                        <button onClick={() => setShowFeedbackModal(false)} className="text-gray-400 hover:text-gray-600"><span className="material-symbols-outlined">close</span></button>
                    </div>
                    
                    {!isFirebaseReady() ? (
                        <div className="bg-red-50 p-4 rounded-xl text-red-600 text-sm mb-4">
                            Je bent offline. Feedback versturen is niet mogelijk.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1 mb-1 block">Soort melding</label>
                                <div className="flex bg-gray-100 dark:bg-black/20 p-1 rounded-xl">
                                    {['suggestion', 'bug', 'other'].map(t => (
                                        <button 
                                            key={t}
                                            onClick={() => setFeedbackType(t)}
                                            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${feedbackType === t ? 'bg-white dark:bg-white/10 shadow text-primary' : 'text-gray-500'}`}
                                        >
                                            {t === 'suggestion' ? 'Idee' : t === 'bug' ? 'Fout' : 'Anders'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1 mb-1 block">Onderwerp</label>
                                <input 
                                    autoFocus
                                    value={feedbackSubject} 
                                    onChange={e => setFeedbackSubject(e.target.value)} 
                                    className="w-full bg-gray-50 dark:bg-black/20 rounded-xl px-4 py-3 font-medium border border-gray-200 dark:border-white/10 outline-none focus:border-primary"
                                    placeholder="Korte samenvatting"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1 mb-1 block">Bericht</label>
                                <textarea 
                                    value={feedbackMessage} 
                                    onChange={e => setFeedbackMessage(e.target.value)} 
                                    className="w-full h-32 bg-gray-50 dark:bg-black/20 rounded-xl px-4 py-3 text-sm border border-gray-200 dark:border-white/10 outline-none focus:border-primary resize-none"
                                    placeholder="Heb je een idee, een klacht of wil je iets anders kwijt? Laat het ons weten!"
                                />
                            </div>

                            <button 
                                onClick={handleSendFeedback} 
                                disabled={isSendingFeedback || !feedbackMessage.trim()} 
                                className="w-full py-4 bg-primary text-black font-bold rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSendingFeedback ? <span className="material-symbols-outlined animate-spin">sync</span> : <span className="material-symbols-outlined filled">send</span>}
                                {isSendingFeedback ? 'Versturen...' : 'Versturen'}
                            </button>
                            <p className="text-[10px] text-gray-400 text-center italic mt-2">Meldingen worden direct naar de beheerder gestuurd.</p>
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
  );
};